import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import { ServiceQueuePanel } from "@/components/queue/ServiceQueuePanel";
import {
  listPharmacyQueuePrescriptions,
  pharmacyOrderPath,
} from "@/core/clinical/clinical-store";
import { PRESCRIPTION_SOURCE_LABELS, PRESCRIPTION_STATUS_LABELS } from "@/core/clinical/types";
import { getPatientById } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import {
  getPharmacyFulfillmentDemoGuide,
  resetPharmacyFulfillmentDemo,
  seedPharmacyFulfillmentDemo,
} from "@/core/clinical/demo-pharmacy-fulfillment";
import { isDemoModeEnabled } from "@/core/demo/is-demo-mode-enabled";
import { routes } from "@/router/all_routes";

export function PharmacyOrdersPage() {
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const refresh = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:clinical-prescriptions", refresh);
    return () => window.removeEventListener("hospitol:clinical-prescriptions", refresh);
  }, []);

  const orders = useMemo(() => {
    void refreshKey;
    return listPharmacyQueuePrescriptions();
  }, [refreshKey]);

  const ordersPagination = useClientPagination(orders, [], 25);
  const { pagedItems: pagedOrders } = ordersPagination;

  const demoGuide = useMemo(() => getPharmacyFulfillmentDemoGuide(), [refreshKey]);
  const showDemoGuide = isDemoModeEnabled();

  return (
    <div>
      <PageHeader
        title="Ordonnances à traiter"
        description="Validation stock, comptabilité partielle et livraison."
        actions={
          <Link to={routes.pharmacyInvoice} className="btn btn-outline-secondary btn-sm">
            Vente comptoir
          </Link>
        }
      />

      {showDemoGuide && demoGuide.length > 0 && (
        <div className="ph-invoice-sheet mb-3">
          <h3 className="h6 fw-semibold mb-2">Parcours démo (4 scénarios)</h3>
          <div className="table-responsive ph-diag-table">
            <table className="table table-sm mb-2">
              <thead>
                <tr>
                  <th>Ordonnance</th>
                  <th>Patient</th>
                  <th>Étapes</th>
                </tr>
              </thead>
              <tbody>
                {demoGuide.map((row) => (
                  <tr key={row.tag} className="ph-diag-row">
                    <td className="small" data-label="Ordonnance">
                      <code>{row.tag}</code>
                    </td>
                    <td className="small" data-label="Patient">
                      {row.patient}
                    </td>
                    <td className="small text-muted" data-label="Étapes">
                      {row.steps}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={() => {
              resetPharmacyFulfillmentDemo();
              void seedPharmacyFulfillmentDemo().then(() => setRefreshKey((k) => k + 1));
            }}
          >
            Recharger les données démo
          </button>
        </div>
      )}

      <ServiceQueuePanel
        serviceId="pharmacy"
        defaultUnitName="Pharmacie"
        enableCallNext
        title="Salle d'attente — officine"
      />

      <div className="ph-invoice-sheet">
        {orders.length === 0 ? (
          <p className="text-muted mb-0">
            Aucune ordonnance en attente — les prescriptions médicales apparaissent ici après
            envoi par le médecin.
          </p>
        ) : (
          <div className="table-responsive ph-diag-table">
            <table className="table table-sm ph-dash-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Patient</th>
                  <th>Origine</th>
                  <th>Lignes</th>
                  <th>Statut</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedOrders.map((rx) => {
                  const patient = getPatientById(rx.patientId);
                  const source = rx.source ?? "clinical";
                  return (
                    <tr key={rx.id} className="ph-diag-row">
                      <td data-label="Date" className="small text-muted">
                        {new Date(rx.createdAt).toLocaleString("fr-FR")}
                      </td>
                      <td data-label="Patient" className="fw-medium">
                        {patient ? patientFullName(patient) : "—"}
                      </td>
                      <td data-label="Origine" className="small">
                        {PRESCRIPTION_SOURCE_LABELS[source]}
                      </td>
                      <td data-label="Lignes" className="small">
                        {rx.lines.length} article(s)
                      </td>
                      <td data-label="Statut">
                        <span className={`hosp-rx-badge hosp-rx-badge--${rx.status}`}>
                          {PRESCRIPTION_STATUS_LABELS[rx.status]}
                        </span>
                      </td>
                      <td data-label="Actions" className="text-end">
                        <Link
                          to={pharmacyOrderPath(rx.id)}
                          className="btn btn-primary btn-sm"
                        >
                          Traiter
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <TablePagination
              page={ordersPagination.page}
              pageSize={ordersPagination.pageSize}
              totalItems={ordersPagination.totalItems}
              onPageChange={ordersPagination.setPage}
              onPageSizeChange={ordersPagination.setPageSize}
            />
          </div>
        )}
      </div>
    </div>
  );
}
